import { useEffect } from "react";
import { setGlobalBodyScrollLocked } from "../../utils/bodyScrollLock";

function isVisible(el) {
  if (!(el instanceof HTMLElement)) return false;
  let node = el;
  while (node) {
    const s = window.getComputedStyle(node);
    if (s.display === "none" || s.visibility === "hidden") return false;
    node = node.parentElement;
  }
  return true;
}

/**
 * Full-screen fixed layers used as modal / drawer backdrops (Tailwind and custom classes).
 */
function isLikelyModalBackdrop(el) {
  if (!(el instanceof HTMLElement)) return false;
  if (el.closest("[data-no-scroll-lock]")) return false;

  const style = window.getComputedStyle(el);
  if (style.position !== "fixed") return false;

  const opacity = parseFloat(style.opacity);
  if (Number.isFinite(opacity) && opacity === 0) return false;

  const rect = el.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (vw <= 0 || vh <= 0) return false;

  const wRatio = rect.width / vw;
  const hRatio = rect.height / vh;

  const coversViewport =
    wRatio >= 0.88 &&
    hRatio >= 0.82 &&
    rect.top <= 96 &&
    rect.left <= 24;

  return coversViewport && isVisible(el);
}

function countModalBackdrops() {
  const nodes = document.querySelectorAll(".fixed");
  let n = 0;
  for (let i = 0; i < nodes.length; i++) {
    if (isLikelyModalBackdrop(nodes[i])) n++;
  }
  return n;
}

/**
 * Watches the DOM for viewport-covering fixed overlays and locks document scroll
 * so only modal interiors (overflow-y-auto, etc.) scroll.
 */
export default function GlobalModalScrollLock() {
  useEffect(() => {
    let raf = 0;

    const sync = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        setGlobalBodyScrollLocked(countModalBackdrops() > 0);
      });
    };

    const mo = new MutationObserver(sync);
    mo.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style"],
    });

    window.addEventListener("resize", sync);
    sync();

    return () => {
      cancelAnimationFrame(raf);
      mo.disconnect();
      window.removeEventListener("resize", sync);
      setGlobalBodyScrollLocked(false);
    };
  }, []);

  return null;
}
